import { useEffect, useMemo, useState } from 'react';
import { Chip } from '@mui/material';
import toast from 'react-hot-toast';
import type { IntegrationSubscription } from '@/types/integration';
import { useEndpoints } from '@/session/useEndpoints';
import { acquireOperationContext, operationRequirement } from '@/session/operation';
import { getSessionSubject } from '@/session/token';
import { peekIntegrationUnhealthy, readIntegrationUnhealthy, writeIntegrationUnhealthy } from '@/session/tenantCache';
import { useOperationRuns } from '@/session/useOperationRuns';
import { utils } from '@/utils';

const UNHEALTHY_LABEL = 'One or more integrations are unhealthy';

export const UnhealthyChip = () => {
  const sessionEndpoints = useEndpoints();
  const operationRuns = useOperationRuns();
  const { organization } = sessionEndpoints;
  const subject = getSessionSubject();
  const cacheIdentity = useMemo(() => (subject ? { version: 2 as const, subject, organization } : null), [organization, subject]);
  const cachedHasUnhealthy = cacheIdentity ? peekIntegrationUnhealthy(cacheIdentity) : null;
  const [fetchedState, setFetchedState] = useState<{ subject: string; organization: string; hasUnhealthy: boolean } | null>(null);
  const hasUnhealthy =
    fetchedState?.subject === subject && fetchedState.organization === organization ? fetchedState.hasUnhealthy : (cachedHasUnhealthy ?? false);

  useEffect(() => {
    if (!cacheIdentity) return;
    const cached = readIntegrationUnhealthy(cacheIdentity);
    if (cached !== null) return;

    const operation = acquireOperationContext(organization);
    const run = operationRuns.begin('integration-unhealthy', operation);

    void sessionEndpoints.integrationData
      .getIntegrationSubscriptions({}, { signal: operation.scopeSignal, operationRequirement: operationRequirement(operation, 'scope') })
      .then((response) => {
        if (response.isError) {
          if (!utils.error.isCanceledResponse(response)) run.publish(() => toast.error('Failed to retrieve subscriptions'));
          return;
        }
        const isUnhealthy = Boolean(
          response.result?.find(
            (subscription: IntegrationSubscription) => subscription.integration_subscription_health?.is_healthy === false && subscription.enabled
          )
        );
        run.publish(() => {
          writeIntegrationUnhealthy(cacheIdentity, isUnhealthy);
          setFetchedState({ subject: cacheIdentity.subject, organization: cacheIdentity.organization, hasUnhealthy: isUnhealthy });
        });
      });

    return () => run.invalidate();
  }, [cacheIdentity, operationRuns, organization, sessionEndpoints.integrationData]);

  if (!hasUnhealthy) {
    return null;
  }

  return <Chip label='!' color='error' size='small' aria-label={UNHEALTHY_LABEL} role='img' />;
};
